/**
 * Player configuration.
 *
 * The embed page's backend templates ONE JSON object into the page:
 *
 *   <script>window.__PLAYER_CONFIG__ = { "embedId": "em...", "title": "...", ... }</script>
 *
 * No hidden form inputs, no selector scraping. Everything is typed, defaulted
 * and validated here, in one place.
 */

/**
 * Player UI themes:
 *  - pulse (default): flat full-width control bar with an accent LIVE pill
 *  - frost: macOS/Safari-style floating frosted-glass pill
 *  - noir:  flat UI — solid fills, square bars, one bright accent, no glass
 *  - reel:  pulse re-laid-out — full-width scrubber on its own row, for live rewind
 */
export const THEMES = ['pulse', 'frost', 'noir', 'reel'] as const
export type ThemeName = (typeof THEMES)[number]

export interface PlayerConfig {
  embedId: string
  fallbackEmbedId: string | null
  title: string
  /** Shown with the title on the pause overlay */
  description: string
  theme: ThemeName
  autoplay: boolean
  audioOnly: boolean
  liveRewind: boolean
  rewindWindowSeconds: number
  showViewersCount: boolean
  showLiveBadge: boolean
  disablePauseControl: boolean
  uninterrupted: boolean
  offlineTitle: string
  offlineSubtitle: string
  showOfflineText: boolean
  posterUrl: string | null
  /** Validated CSS color for --theme-color; null = default theme */
  color: string | null
  /** Namespace id used by Mux + viewer analytics */
  nsid: string | null
  /** The embed is password protected. Lets the player go straight back to the
   *  prompt when a RENEWAL is denied, rather than to a generic blocked state. */
  needsPassword: boolean
  /**
   * Live chat, served by the chat app in an iframe beside the video.
   *
   * Defaults to OFF. An embed whose config predates these fields gets no chat
   * rather than a panel the broadcaster never asked for, and the absence of a
   * field is never read as consent.
   */
  chatEnabled: boolean
  /** Viewers can read the room but not post. Owned and enforced entirely by
   *  the chat app, which reads it from its own room config; the player parses
   *  it so watch-guard's payload round-trips, and does nothing with it. */
  chatReadonly: boolean
  /** Shareable watch-page URL for the Share overlay (default: inferred) */
  shareUrl: string | null
  muxEnvKey: string
  debug: boolean
  /**
   * While paused (or before first play) keep re-syncing the preview frame to
   * the live edge every few seconds, so pressing play starts live instantly.
   * Costs a little bandwidth per idle viewer — set false to disable.
   */
  keepPreviewFresh: boolean
  /** Dev override: skip the playback API and play this URL directly */
  preloadHlsUrl: string | null
  /** Dev override for the fallback source */
  backupHlsUrl: string | null
}

interface RawConfig extends Partial<Omit<PlayerConfig, 'color'>> {
  color?: string
}

declare global {
  interface Window {
    __PLAYER_CONFIG__?: RawConfig
  }
}

const DEFAULTS: Omit<PlayerConfig, 'embedId'> = {
  fallbackEmbedId: null,
  title: '',
  description: '',
  theme: 'pulse',
  autoplay: true,
  audioOnly: false,
  liveRewind: false,
  rewindWindowSeconds: 3600,
  showViewersCount: true,
  showLiveBadge: true,
  disablePauseControl: false,
  uninterrupted: false,
  offlineTitle: 'Sorry, we are not live right now',
  offlineSubtitle: 'Please check back at a later time',
  showOfflineText: true,
  posterUrl: null,
  color: null,
  nsid: null,
  needsPassword: false,
  chatEnabled: false,
  chatReadonly: false,
  shareUrl: null,
  muxEnvKey: 'ttu9mduc63f4qmg0ktu6v5t7h',
  debug: false,
  keepPreviewFresh: true,
  preloadHlsUrl: null,
  backupHlsUrl: null,
}

/** Only accept values the browser agrees are a color — never inject raw strings into CSS. */
function sanitizeColor(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0 || value.length > 64) return null
  try {
    return CSS.supports('color', value) ? value : null
  } catch {
    return null
  }
}

/**
 * Audio-only is a per-REQUEST choice, not a property of the embed: every stream
 * can be played as audio. `?audio_only=true` replaces the old `<id>_audio.html`
 * variant, and the server mints a manifest filtered to the audio track.
 *
 * Parsed loosely — these URLs get hand-edited by customers.
 */
export function audioOnlyFromLocation(): boolean {
  const raw = new URLSearchParams(window.location.search).get('audio_only')
  if (raw === null) return false
  const value = raw.trim().toLowerCase()
  return value === '' || value === '1' || value === 'true' || value === 'yes'
}

/**
 * Path segments that carry no id: routing prefixes, and the index page itself.
 * `live` is the one production uses, and the reason this function had to stop
 * assuming the id is always the whole path.
 */
const PATH_PREFIXES = new Set(['live', 'embed', 'watch', 'player', 'index'])

/**
 * The embed id from the URL.
 *
 * Accepts any depth, because the watch host serves several shapes and has to
 * keep serving the old ones: /<id>, /live/<id>, /embed/<id>, and ?embedId=<id>
 * are all the same page. Reading the LAST segment that is not a routing prefix
 * handles all of them without a route table, and keeps working when a prefix
 * is added in front of an id that already works.
 *
 * A trailing slash, an .html suffix and percent-encoding are all tolerated;
 * anything left over falls through to the query parameter, which is what an
 * iframe embed uses.
 */
export function embedIdFromLocation(): string {
  const segments = window.location.pathname.split('/').filter(Boolean)
  for (let i = segments.length - 1; i >= 0; i--) {
    let seg = segments[i]
    try {
      seg = decodeURIComponent(seg)
    } catch {
      /* malformed escape: use it as it came */
    }
    seg = seg.replace(/\.html$/, '')
    if (seg && !PATH_PREFIXES.has(seg)) return seg
  }
  return new URLSearchParams(window.location.search).get('embedId') ?? ''
}

/**
 * Shape and default a config object.
 *
 * The object arrives from watch-guard's authorize response (it is specified as
 * this file's exact wire shape), or from `window.__PLAYER_CONFIG__` when a dev
 * or self-hosted page supplies one. Everything below applies either way:
 * unknown themes fall back, colours are validated, and the audio-only and
 * uninterrupted couplings are enforced last so they cannot be contradicted.
 */
export function buildConfig(raw: RawConfig, overrides: ConfigOverrides = {}): PlayerConfig {
  const embedId =
    (typeof raw.embedId === 'string' && raw.embedId.trim()) || overrides.embedId || embedIdFromLocation()

  const config: PlayerConfig = {
    ...DEFAULTS,
    // null is pruned, so a server null falls back to the default rather than
    // blanking a field the player needs (muxEnvKey being the one that matters).
    ...pruneUndefined(raw),
    embedId,
    color: sanitizeColor(raw.color),
    theme: THEMES.includes(raw.theme as ThemeName) ? (raw.theme as ThemeName) : 'pulse',
  }

  if (overrides.audioOnly) config.audioOnly = true

  if (config.audioOnly) {
    // Audio-only embeds never autoplay and always allow pausing.
    config.autoplay = false
    config.uninterrupted = false
    config.disablePauseControl = false
  }

  if (config.uninterrupted) {
    config.autoplay = true
  }

  return config
}

export interface ConfigOverrides {
  embedId?: string
  /** From `?audio_only=true` — wins over whatever the embed record says */
  audioOnly?: boolean
}

/**
 * Dev / self-host escape hatch: a page that templates its own config skips the
 * API entirely. Also the rollback path — with `preloadHlsUrl` set, the player
 * plays an untokenised URL exactly as it did before watch-guard existed.
 */
export function devConfigOverride(): PlayerConfig | null {
  const raw = window.__PLAYER_CONFIG__
  if (!raw) return null
  return buildConfig(raw, { audioOnly: audioOnlyFromLocation() })
}

function pruneUndefined<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null) (out as Record<string, unknown>)[key] = value
  }
  return out
}
